import SideBarContainer from 'Containers/SideBarContainer';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsDataGrid from 'Pages/reports/components/ReportsDataGrid/ReportsDataGrid';
import { ReportsButtonGroup } from 'Pages/reports/components/ReportsButtonGroup/ReportsButtonGroup';
import HistoryReportFilters from './components/HistoryReportFilters/HistoryReportFilters';
import useHistoryReportPage from './hooks/useHistoryReportPage';
import { Padding } from 'Components/Padding';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import { buildTime } from 'Pages/reports/utils/buildTime';
import { toReportsPagination } from 'Pages/reports/utils/pagination';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import type { GridColDef } from '@mui/x-data-grid';

interface Row {
  id: string;
  payroll: string | number;
  fullName: string;
  terminal: string;
  department: string;
  jobTitle: string;
  gender: string;
  age: number | string;
  entryDate: string;
  totalCoursesAssigned: number | string;
  pendingCourses: number | string;
  completedCourses: number | string;
  trainingHours: string;
}

export default function Page() {
  const {
    historyReportData,
    historyReportPagination,
    historyReportLoading,
    historyReportError,
    t2,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    handleRowClick,
  } = useHistoryReportPage();

  const buttons = [
    {
      icon: <EmployeesReportIcon />,
      tooltip: t2('employees_report_tooltip'),
      route: '/reporte/historial/empleados',
    },
    { icon: <CoursesReportIcon />, tooltip: t2('courses_report_tooltip'), route: '/reporte/historial/cursos' },
  ];

  const columns: GridColDef[] = [
    {
      field: 'payroll',
      headerName: t2('payroll_number'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'fullName', headerName: t2('full_name'), flex: 1.5 },
    { field: 'terminal', headerName: t2('terminal'), flex: 1 },
    { field: 'department', headerName: t2('department'), flex: 1 },
    { field: 'jobTitle', headerName: t2('job_title'), flex: 1 },
    { field: 'gender', headerName: t2('gender'), flex: 0.8 },
    {
      field: 'age',
      headerName: t2('age'),
      flex: 0.6,
      align: 'center',
      headerAlign: 'center',
    },
    { field: 'entryDate', headerName: t2('entry_date'), flex: 1 },
    {
      field: 'totalCoursesAssigned',
      headerName: t2('total_courses'),
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'pendingCourses',
      headerName: t2('pending_courses'),
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'completedCourses',
      headerName: t2('completed_courses'),
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'trainingHours',
      headerName: t2('training_hours'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  const rows: Row[] = historyReportData.map(item => ({
    id: item.id,
    payroll: item.payroll,
    fullName: item.fullName,
    terminal: item.terminal || '-',
    department: item.department || '-',
    jobTitle: item.jobTitle || '-',
    gender: item.gender || '-',
    age: item.age ?? '-',
    entryDate: item.entryDate || '-',
    totalCoursesAssigned: item.totalCoursesAssigned === 0 ? '-' : item.totalCoursesAssigned,
    pendingCourses: item.pendingCourses === 0 ? '-' : item.pendingCourses,
    completedCourses: item.completedCourses === 0 ? '-' : item.completedCourses,
    trainingHours: buildTime(item.trainingHours, t2),
  }));

  return (
    <SideBarContainer>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={t2('title_accent')} />}
        buttons={<ReportsButtonGroup buttons={buttons} />}
        onExport={handleExport}
      />
      <Padding padding="1rem">
        <HistoryReportFilters onSearch={handleSearch} />
        <Padding padding="0.5rem 0" />
        <ReportsDataGrid<Row>
          columns={columns}
          rows={rows}
          loading={historyReportLoading}
          error={historyReportError}
          pagination={toReportsPagination(historyReportPagination)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onRowClick={row => handleRowClick(row)}
        />
      </Padding>
    </SideBarContainer>
  );
}
